import {
  cloneElement,
  isValidElement,
  type HTMLAttributes,
  type JSX,
  type ReactElement,
  type ReactNode,
} from 'react';
import clsx from 'clsx';
import { InfoCircledIcon } from '@radix-ui/react-icons';
import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from '@radix-ui/react-popover';

const joinIds = (ids: Array<string | undefined>): string | undefined => {
  const filtered = ids.filter((value) => value !== undefined);
  if (filtered.length === 0) {
    return undefined;
  }
  return filtered.join(' ');
};

type ControlProps = {
  'aria-describedby'?: string;
  className?: string;
  type?: string;
};

const shouldForceFullWidth = (element: ReactElement): boolean => {
  if (typeof element.type !== 'string') {
    return false;
  }

  if (element.type === 'input') {
    const inputType = (element.props as { type?: string }).type;
    return inputType !== 'checkbox' && inputType !== 'radio';
  }

  return element.type === 'select' || element.type === 'textarea';
};

const shouldApplyRequired = (element: ReactElement): boolean => {
  if (typeof element.type !== 'string') {
    return false;
  }

  return element.type === 'input' || element.type === 'select' || element.type === 'textarea';
};

export interface FormFieldProps extends HTMLAttributes<HTMLDivElement> {
  id: string;
  label: string;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  hint?: ReactNode;
  labelSecondary?: ReactNode;
}

export function FormField({
  id,
  label,
  description,
  error,
  required,
  children,
  hint,
  labelSecondary,
  ...rest
}: FormFieldProps): JSX.Element {
  const descriptionId =
    description !== undefined && description !== null ? `${id}-description` : undefined;
  const hasError = error !== undefined && error !== null;
  const errorId = hasError ? `${id}-error` : undefined;
  const hintId = hint !== undefined && hint !== null ? `${id}-hint` : undefined;

  const describedBy = joinIds([hintId, descriptionId, errorId]);
  const hasPopover = description !== undefined || hint !== undefined;
  const popoverId = hasPopover ? `${id}-help` : undefined;

  let control = children;

  const buildControlProps = (element: ReactElement<ControlProps>) => {
    const existingDescribedBy = element.props['aria-describedby'];
    const mergedDescribedBy = joinIds([existingDescribedBy, describedBy]);
    const nextProps: Record<string, unknown> = {
      id,
      'aria-describedby': mergedDescribedBy,
      'aria-invalid': hasError ? true : undefined,
    };

    if (required === true && shouldApplyRequired(element)) {
      nextProps.required = true;
    }

    if (shouldForceFullWidth(element)) {
      nextProps.className = clsx(element.props.className, 'w-full');
    }

    return nextProps;
  };

  if (isValidElement(children)) {
    const element = children as ReactElement<ControlProps>;
    control = cloneElement(element, buildControlProps(element));
  } else if (Array.isArray(children)) {
    control = (children as ReactNode[]).map((child, index) => {
      if (index === 0 && isValidElement(child)) {
        const element = child as ReactElement<ControlProps>;
        return cloneElement(element, buildControlProps(element));
      }
      return child;
    });
  }

  const labelContent = (
    <span className="inline-flex items-center gap-1">
      <span>{label}</span>
      {required === true ? (
        <span className="text-red-600" aria-hidden="true">
          *
        </span>
      ) : null}
    </span>
  );
  const popoverContent = hasPopover ? (
    <div className="flex flex-col gap-1 text-xs text-white">
      {description !== undefined && description !== null ? <span>{description}</span> : null}
      {hint !== undefined && hint !== null ? <span className="text-white/80">{hint}</span> : null}
    </div>
  ) : null;
  const labelNode = (
    <label htmlFor={id} className="text-sm font-medium text-text-primary">
      <span className="inline-flex items-center gap-2">
        {labelContent}
        {popoverContent ? (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`More info about ${label}`}
                aria-haspopup="dialog"
                aria-controls={popoverId}
                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-text-primary/60 transition hover:text-brand-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
              >
                <InfoCircledIcon className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverPortal>
              <PopoverContent
                id={popoverId}
                side="top"
                align="center"
                sideOffset={8}
                collisionPadding={12}
                aria-label={`${label} help`}
                className="max-w-xs rounded-lg border border-brand-primary/20 bg-brand-primary px-3 py-2 text-xs text-white shadow-lg"
              >
                {popoverContent}
              </PopoverContent>
            </PopoverPortal>
          </Popover>
        ) : null}
      </span>
    </label>
  );

  return (
    <div className="flex flex-col gap-xs" {...rest}>
      {labelSecondary !== undefined && labelSecondary !== null ? (
        <div className="flex items-center justify-between gap-sm">
          {labelNode}
          <div>{labelSecondary}</div>
        </div>
      ) : (
        labelNode
      )}
      {hint !== undefined && hint !== null ? (
        <p id={hintId} className="text-xs text-text-primary/70">
          {hint}
        </p>
      ) : null}
      <div className={clsx('relative', hasError ? 'mb-5' : null)}>
        {control}
        {hasError ? (
          <p
            id={errorId}
            className="absolute left-0 top-full mt-xs text-xs font-medium text-red-600"
          >
            {error}
          </p>
        ) : null}
      </div>
      {description !== undefined && description !== null ? (
        <p id={descriptionId} className="text-xs text-text-primary/70">
          {description}
        </p>
      ) : null}
    </div>
  );
}
